import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Toast, ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './toast-container.component.html',
  styleUrl: './toast-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToastContainerComponent {
  toastService = inject(ToastService);

  getToastClasses(toast: Toast): string {
    const baseClasses = [
      'relative',
      'w-full',
      'bg-white',
      'shadow-xl',
      'rounded-xl',
      'pointer-events-auto',
      'border-l-4',
      'overflow-hidden',
      'toast-enter',
      'backdrop-blur-sm',
      'transition-all',
      'duration-300',
      'hover:shadow-2xl',
      'hover:scale-105'
    ];

    const typeClasses = {
      success: ['border-l-green-500', 'shadow-green-100'],
      error: ['border-l-red-500', 'shadow-red-100'],
      warning: ['border-l-amber-500', 'shadow-amber-100'],
      info: ['border-l-blue-500', 'shadow-blue-100']
    };

    return [...baseClasses, ...typeClasses[toast.type]].join(' ');
  }

  getIconBackgroundClasses(type: Toast['type']): string {
    const classes = [
      'flex',
      'items-center',
      'justify-center',
      'w-8',
      'h-8',
      'rounded-full'
    ];

    const typeClasses = {
      success: ['bg-green-100', 'text-green-600'],
      error: ['bg-red-100', 'text-red-600'],
      warning: ['bg-amber-100', 'text-amber-600'],
      info: ['bg-blue-100', 'text-blue-600']
    };

    return [...classes, ...typeClasses[type]].join(' ');
  }

  getIconClasses(type: Toast['type']): string {
    return 'current-color';
  }

  getTypeLabel(type: Toast['type']): string {
    const labelClasses = {
      success: 'text-green-600',
      error: 'text-red-600',
      warning: 'text-amber-600',
      info: 'text-blue-600'
    };

    return labelClasses[type];
  }

  getTypeName(type: Toast['type']): string {
    const typeNames = {
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      info: 'Info'
    };

    return typeNames[type];
  }

  getCloseButtonClasses(type: Toast['type']): string {
    const baseClasses = [
      'inline-flex',
      'items-center',
      'justify-center',
      'w-6',
      'h-6',
      'rounded-full',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-offset-2',
      'transition-colors',
      'duration-200'
    ];

    const typeClasses = {
      success: ['text-green-400', 'hover:text-green-600', 'hover:bg-green-50', 'focus:ring-green-500'],
      error: ['text-red-400', 'hover:text-red-600', 'hover:bg-red-50', 'focus:ring-red-500'],
      warning: ['text-amber-400', 'hover:text-amber-600', 'hover:bg-amber-50', 'focus:ring-amber-500'],
      info: ['text-blue-400', 'hover:text-blue-600', 'hover:bg-blue-50', 'focus:ring-blue-500']
    };

    return [...baseClasses, ...typeClasses[type]].join(' ');
  }

  dismiss(id: string): void {
    this.toastService.dismiss(id);
  }
}